import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Box,
    Divider,
    List,
    ListItemButton,
    ListItemText,
    Tooltip,
    Typography,
    useTheme,
} from '@mui/material';
import { DragIndicator } from '@mui/icons-material';
import {
    DndContext,
    KeyboardSensor,
    PointerSensor,
    closestCenter,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    SortableContext,
    arrayMove,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ehCompartilhado } from '../../utils/compartilhamentoDoFiltro';
import {
    GRUPO_COMPARTILHADO,
    GRUPO_PESSOAL,
    filtrosNaOrdemDoOperador,
    gravarOrdem,
} from '../../utils/ordemDosFiltros';

/**
 * A lista de filtros e Kanbans do painel lateral da tela de chamados.
 *
 * ## Por que é um componente, e não o trecho de JSX que era
 *
 * Isto morava dentro de uma IIFE na `TicketPage`, que tem seis mil linhas e
 * nenhum teste. O ponto 22 da Gisele ("cada operador pode reordenar os
 * Kanbans, e se possível a preferência fica salva") acrescenta estado,
 * persistência e arraste a ela -- três coisas que ninguém consegue conferir
 * lá dentro.
 *
 * ## Dois blocos, duas ordens
 *
 * Compartilhados em cima, visualizações pessoais embaixo. A separação é a que
 * já existia, e olha os DOIS campos de compartilhamento
 * (`utils/compartilhamentoDoFiltro`): olhar só `is_public` jogava o filtro de
 * equipe criado pelo caminho com permissão no bloco errado.
 *
 * Cada bloco se reordena por conta própria. Arrastar entre blocos não existe:
 * a posição pedida não teria como ser mostrada neste desenho.
 */
const ListaDeFiltros = ({ filtros, filtroSelecionado, operadorId, onSelecionar }) => {
    const theme = useTheme();

    const visiveis = useMemo(
        () => (filtros || []).filter((f) => f.settings?.visible_in_panel !== false),
        [filtros],
    );

    const daApi = useMemo(() => ({
        [GRUPO_COMPARTILHADO]: visiveis.filter((f) => ehCompartilhado(f)),
        [GRUPO_PESSOAL]: visiveis.filter((f) => !ehCompartilhado(f)),
    }), [visiveis]);

    /*
     * A ordem fica em estado, e não é recalculada a cada render, porque
     * arrastar tem de mostrar a nova posição NA HORA -- esperar o
     * `localStorage` e um novo render do pai deixaria o cartão voltando ao
     * lugar antigo por um instante.
     *
     * O `useEffect` abaixo reconcilia quando a lista da api muda.
     */
    const [ordenados, setOrdenados] = useState(() => ({
        [GRUPO_COMPARTILHADO]: filtrosNaOrdemDoOperador(daApi[GRUPO_COMPARTILHADO], operadorId, GRUPO_COMPARTILHADO),
        [GRUPO_PESSOAL]: filtrosNaOrdemDoOperador(daApi[GRUPO_PESSOAL], operadorId, GRUPO_PESSOAL),
    }));

    useEffect(() => {
        setOrdenados({
            [GRUPO_COMPARTILHADO]: filtrosNaOrdemDoOperador(daApi[GRUPO_COMPARTILHADO], operadorId, GRUPO_COMPARTILHADO),
            [GRUPO_PESSOAL]: filtrosNaOrdemDoOperador(daApi[GRUPO_PESSOAL], operadorId, GRUPO_PESSOAL),
        });
    }, [daApi, operadorId]);

    const sensores = useSensors(
        /*
         * `distance: 5` é o que separa clicar de arrastar: sem a distância
         * mínima, selecionar um filtro com o mouse um pouco trêmulo viraria um
         * arraste de meio pixel, e o clique nunca chegava ao `onSelecionar`.
         */
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );

    const [avisoDeGravacao, setAvisoDeGravacao] = useState(false);

    const reordenar = useCallback((grupo) => ({ active, over }) => {
        if (!over || active.id === over.id) return;

        setOrdenados((atual) => {
            const lista = atual[grupo];
            const de = lista.findIndex((f) => String(f.id) === String(active.id));
            const para = lista.findIndex((f) => String(f.id) === String(over.id));
            if (de < 0 || para < 0) return atual;

            const nova = arrayMove(lista, de, para);

            // Avisa quando NÃO conseguiu gravar (aba privada, dado de site
            // bloqueado). A ordem vale nesta sessão de qualquer jeito; o que
            // não sobrevive é o recarregar, e prometer isso em silêncio seria
            // pior do que dizer.
            setAvisoDeGravacao(!gravarOrdem(operadorId, grupo, nova.map((f) => f.id)));

            return { ...atual, [grupo]: nova };
        });
    }, [operadorId]);

    if (visiveis.length === 0) {
        return (
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary, p: 1 }}>
                Nenhum filtro disponível
            </Typography>
        );
    }

    const compartilhados = ordenados[GRUPO_COMPARTILHADO];
    const pessoais = ordenados[GRUPO_PESSOAL];

    const bloco = (grupo, lista, margemAbaixo) => (
        <DndContext
            sensors={sensores}
            collisionDetection={closestCenter}
            onDragEnd={reordenar(grupo)}
        >
            <SortableContext
                items={lista.map((f) => String(f.id))}
                strategy={verticalListSortingStrategy}
            >
                <List
                    dense
                    disablePadding
                    sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: margemAbaixo ? 1 : 0 }}
                >
                    {lista.map((f) => (
                        <FiltroArrastavel
                            key={f.id}
                            filtro={f}
                            selecionado={filtroSelecionado?.id === f.id}
                            onSelecionar={onSelecionar}
                            theme={theme}
                        />
                    ))}
                </List>
            </SortableContext>
        </DndContext>
    );

    return (
        <>
            {compartilhados.length > 0 && bloco(GRUPO_COMPARTILHADO, compartilhados, pessoais.length > 0)}

            {pessoais.length > 0 && (
                <>
                    {compartilhados.length > 0 && (
                        <>
                            <Divider sx={{ my: 1 }} />
                            <Typography
                                variant="caption"
                                sx={{
                                    color: `${theme.palette.text.primary} !important`,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.08em',
                                    fontWeight: 400,
                                    mb: 0.5,
                                    px: 0.5,
                                }}
                            >
                                Visualizações pessoais
                            </Typography>
                        </>
                    )}
                    {bloco(GRUPO_PESSOAL, pessoais, false)}
                </>
            )}

            {avisoDeGravacao && (
                <Typography variant="caption" sx={{ display: 'block', color: theme.palette.text.secondary, px: 0.5, mt: 1 }}>
                    A ordem vale nesta sessão, mas não foi possível salvá-la neste navegador.
                </Typography>
            )}
        </>
    );
};

/**
 * Um filtro da lista, arrastável pela alça.
 *
 * A alça existe em vez de o item todo arrastar porque o item todo é o BOTÃO de
 * selecionar o filtro: é o que o operador faz dezenas de vezes por dia, e
 * reordenar é o que ele faz uma vez. Quem tem de ficar fácil é o clique.
 */
const FiltroArrastavel = ({ filtro, selecionado, onSelecionar, theme }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: String(filtro.id) });

    return (
        <ListItemButton
            ref={setNodeRef}
            data-testid={`filtro-${filtro.id}`}
            selected={selecionado}
            onClick={() => onSelecionar(filtro)}
            style={{
                transform: CSS.Transform.toString(transform),
                transition,
                opacity: isDragging ? 0.5 : 1,
            }}
            sx={{
                borderRadius: 1.5,
                border: `1px solid ${selecionado ? theme.palette.primary.main : theme.palette.divider}`,
                alignItems: 'flex-start',
                py: 0.75,
                gap: 0.5,
            }}
        >
            <Tooltip title="Arraste para reordenar" placement="left">
                {/*
                  * `onClick` com `stopPropagation` além da alça do dnd-kit: o
                  * arraste só começa depois de 5px, então um clique seco na
                  * alça escorreria para o `ListItemButton` e trocaria o filtro
                  * selecionado -- exatamente o que quem foi reordenar não quer.
                  */}
                <Box
                    component="span"
                    aria-label={`Reordenar ${filtro.name}`}
                    data-testid={`arrastar-${filtro.id}`}
                    onClick={(e) => e.stopPropagation()}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        cursor: 'grab',
                        color: theme.palette.text.disabled,
                        touchAction: 'none',
                        mt: '2px',
                        '&:hover': { color: theme.palette.text.secondary },
                    }}
                    {...attributes}
                    {...listeners}
                >
                    <DragIndicator fontSize="small" />
                </Box>
            </Tooltip>
            <ListItemText
                primary={filtro.name}
                primaryTypographyProps={{ sx: { fontWeight: 400, color: theme.palette.text.primary } }}
            />
        </ListItemButton>
    );
};

export default ListaDeFiltros;
